// src/app/services/tenant-config.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

export enum MenuStyle {
  SIDE_MENU = 'side-menu',
  TOP_MENU = 'top-menu',
}
export enum FooterStyle {
  SIMPLE = 'simple',
  DETAILED = 'detailed',
}
export enum AppTheme {
  MODERN = 'modern',
  CLASSIC = 'classic',
}
export enum DialogsTheme {
  ASIDE = 'aside',
  BOTTOM_SHEET = 'bottom_sheet',
  MODAL = 'modal',
}
export interface TenantConfig {
  coloredLogoUrl: string;
  whiteLogoUrl: string;
  sidePageImageUrl: string;
  defaultOpportunityImageUrl: string;
  homeMobileAppSplashImageUrl?: string;
  emptyStateImageUrl?: string;
  primaryColor: string;
  secondaryColor: string;
  mediumPrimaryColor: string;
  lightPrimaryColor: string;
  successColor: string;
  errorColor: string;
  warningColor: string;
  infoColor: string;
  tenantId: string;
  companyName: string;
  companyNameAr?: string;
  tenantName: string;
  title?: string;
  favIcon?: string;
  notificationImg?: string;
  faviconUrl?: string;
  fontFamily?: string;
  fontFamilyEn?: string;
  fontFamilyAr?: string;
  fontUrl?: string;
  fontUrlEn?: string;
  fontUrlAr?: string;
  fontUrls?: string[];
  apisBaseUrl: string;
  notificationServiceBaseUrl: string;
  awsUrl: string;
  mapKey: string;
  moyassarPublishableKey: string;
  themeCssPath?: string; // optional explicit path; defaults to assets/themes/{tenantId}.css
  menuStyle: MenuStyle;
  showWelcomeMessageInAuthScreens: boolean;
  showInvestTypesBeforeRegistration: boolean;
  opportunityTheme: AppTheme;
  opportunityDetailsTheme: AppTheme;
  profileSettingsTheme: AppTheme;
  footerStyle: FooterStyle;
  dialogsTheme: DialogsTheme;
  hasLandingPage: boolean;
  googlePlayDownloadUrl: string;
  appleStoreDownloadUrl: string;
  twitterUrl: string;
  linkedinUrl: string;
  supportEmail: string;
  tollFreeNumber?: string;
  isOnlinePaymentEnabled: boolean;
  isArabBankTransferEnabled: boolean;
  colors?: {
    text?: {
      primary?: string;
      secondary?: string;
      disabled?: string;
      brand?: string;
      inverse?: string;
      error?: string;
      success?: string;
      warning?: string;
      info?: string;
    };
    [key: string]: any;
  };
  typography?: {
    fontSizes?: Record<string, string>;
    lineHeights?: Record<string, string>;
    letterSpacings?: Record<string, string>;
    fontWeights?: Record<string, string>;
  };
  input?: any;
  button?: any;
  spacing?: Record<string, string>;
  borderRadius?: Record<string, string>;
  boxShadow?: Record<string, string>;
}

@Injectable({ providedIn: 'root' })
export class TenantConfigService {
  private config!: TenantConfig;

  constructor(private http: HttpClient) {}

  async loadConfig(): Promise<void> {
    try {
      const timestamp = new Date().getTime();
      const raw: any = await firstValueFrom(
        this.http.get(`/assets/config/app-config.json?t=${timestamp}`),
      );

      // Coerce string values from environment into strongly-typed enums
      this.config = {
        ...raw,
        apisBaseUrl: environment.appConfig?.apisBaseUrl || raw.apisBaseUrl,
        notificationServiceBaseUrl:
          environment.appConfig?.notificationServiceBaseUrl ||
          raw.notificationServiceBaseUrl,
        awsUrl: environment.appConfig?.awsUrl || raw.awsUrl,
        moyassarPublishableKey:
          environment.appConfig?.moyassarPublishableKey ||
          raw.moyassarPublishableKey,
        mapKey: environment.mapKey || raw.mapKey,
        menuStyle: raw.menuStyle as MenuStyle,
        footerStyle: raw.footerStyle as FooterStyle,
        opportunityTheme: raw.opportunityTheme as AppTheme,
        opportunityDetailsTheme: raw.opportunityDetailsTheme as AppTheme,
        dialogsTheme: raw.dialogsTheme as DialogsTheme,
      } as TenantConfig;
      this.applyTheme(this.config);
    } catch (error) {
      console.error('Failed to load app config', error);
    }
  }

  get tenant(): TenantConfig {
    return this.config;
  }

  private applyTheme(cfg: TenantConfig) {
    const root = document.documentElement;
    if (!root) {
      return;
    }

    if (cfg && cfg.primaryColor) {
      root.style.setProperty('--primary-color', cfg.primaryColor as string);
    }

    if (cfg && cfg.secondaryColor) {
      root.style.setProperty('--secondary-color', cfg.secondaryColor as string);
    }

    if (cfg && cfg.mediumPrimaryColor) {
      root.style.setProperty(
        '--medium-primary-color',
        cfg.mediumPrimaryColor as string,
      );
    }

    if (cfg && cfg.lightPrimaryColor) {
      root.style.setProperty(
        '--light-primary-color',
        cfg.lightPrimaryColor as string,
      );
    }

    if (cfg && cfg.successColor) {
      root.style.setProperty('--success-color', cfg.successColor as string);
    }

    if (cfg && cfg.errorColor) {
      root.style.setProperty('--error-color', cfg.errorColor as string);
    }

    if (cfg && cfg.warningColor) {
      root.style.setProperty('--warning-color', cfg.warningColor as string);
    }

    if (cfg && cfg.infoColor) {
      root.style.setProperty('--info-color', cfg.infoColor as string);
    }

    if (cfg && cfg.title) {
      document.title = cfg.title;
    } else if (cfg && cfg.tenantName) {
      document.title = cfg.tenantName;
    }

    if (cfg) {
      if (cfg.fontFamily) {
        root.style.setProperty('--primary-font-family', cfg.fontFamily);
      }
      if (cfg.fontFamilyEn || cfg.fontFamily) {
        root.style.setProperty(
          '--primary-font-family-en',
          cfg.fontFamilyEn || cfg.fontFamily || 'sans-serif',
        );
      }
      if (cfg.fontFamilyAr || cfg.fontFamily) {
        root.style.setProperty(
          '--primary-font-family-ar',
          cfg.fontFamilyAr || cfg.fontFamily || 'sans-serif',
        );
      }
    }

    const fontUrlsToLoad: string[] = [];
    if (cfg && cfg.fontUrl) {
      fontUrlsToLoad.push(cfg.fontUrl);
    }
    if (cfg && cfg.fontUrlEn) {
      fontUrlsToLoad.push(cfg.fontUrlEn);
    }
    if (cfg && cfg.fontUrlAr) {
      fontUrlsToLoad.push(cfg.fontUrlAr);
    }
    if (cfg && cfg.fontUrls && Array.isArray(cfg.fontUrls)) {
      fontUrlsToLoad.push(...cfg.fontUrls);
    }

    const uniqueFontUrls = Array.from(new Set(fontUrlsToLoad.filter(u => u && u.trim().length > 0)));

    uniqueFontUrls.forEach((url, idx) => {
      const linkId = `dynamic-tenant-font-${idx}`;
      let fontLink = document.getElementById(linkId) as HTMLLinkElement | null;
      if (!fontLink) {
        fontLink = document.createElement('link');
        fontLink.id = linkId;
        fontLink.rel = 'stylesheet';
        document.head.appendChild(fontLink);
      }
      fontLink.href = url;
    });
    // Provide CSS vars for tenant assets used in global styles
    root.style.setProperty(
      '--tenant-side-image',
      `url('${cfg.sidePageImageUrl}')`,
    );
    let tenantLogo = cfg.favIcon || cfg.coloredLogoUrl;
    if (cfg && tenantLogo) {
      const existing = document.querySelector(
        'link[rel="icon"]',
      ) as HTMLLinkElement | null;
      const link: HTMLLinkElement = existing || document.createElement('link');
      link.rel = 'icon';
      link.href = tenantLogo;
      if (tenantLogo.endsWith('.svg')) {
        link.type = 'image/svg+xml';
      } else if (tenantLogo.endsWith('.png')) {
        link.type = 'image/png';
      } else if (tenantLogo.endsWith('.ico')) {
        link.type = 'image/x-icon';
      }
      if (!existing) {
        document.head.appendChild(link);
      }
    }

    // Inject or swap tenant stylesheet
    const themeLinkId = 'tenant-theme-styles';
    const themeHref =
      (cfg.themeCssPath && cfg.themeCssPath.trim()) ||
      `assets/themes/${cfg.tenantId}.css`;
    let themeLink = document.getElementById(
      themeLinkId,
    ) as HTMLLinkElement | null;
    if (!themeLink) {
      themeLink = document.createElement('link');
      themeLink.id = themeLinkId;
      themeLink.rel = 'stylesheet';
      document.head.appendChild(themeLink);
    }
    // Only update if different (account for absolute href resolution)
    const currentHref = themeLink.getAttribute('href');
    if (currentHref !== themeHref) {
      themeLink.setAttribute('href', themeHref);
    }

    if (cfg.colors) {
      this.applyCssVariablesToDom(cfg.colors, root, 'color');
    }
    if (cfg.typography) {
      if (cfg.typography.fontSizes) {
        this.applyCssVariablesToDom(
          cfg.typography.fontSizes,
          root,
          'font-size',
        );
      }
      if (cfg.typography.lineHeights) {
        this.applyCssVariablesToDom(
          cfg.typography.lineHeights,
          root,
          'line-height',
        );
      }
      if (cfg.typography.letterSpacings) {
        this.applyCssVariablesToDom(
          cfg.typography.letterSpacings,
          root,
          'letter-spacing',
        );
      }
      if (cfg.typography.fontWeights) {
        this.applyCssVariablesToDom(
          cfg.typography.fontWeights,
          root,
          'font-weight',
        );
      }
    }
    if (cfg.input) {
      this.applyCssVariablesToDom(cfg.input, root, 'input');
    }
    if (cfg.button) {
      this.applyCssVariablesToDom(cfg.button, root, 'button');
    }
    if (cfg.spacing) {
      this.applyCssVariablesToDom(cfg.spacing, root, 'spacing');
    }
    if (cfg.borderRadius) {
      this.applyCssVariablesToDom(cfg.borderRadius, root, 'border-radius');
    }
    if (cfg.boxShadow) {
      this.applyCssVariablesToDom(cfg.boxShadow, root, 'box-shadow');
    }
  }

  private applyCssVariablesToDom(
    obj: any,
    root: HTMLElement,
    prefix: string = '',
  ) {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const val = obj[key];
        const cssKey = prefix ? `${prefix}-${key}` : key;
        if (typeof val === 'object' && val !== null) {
          this.applyCssVariablesToDom(val, root, cssKey);
        } else if (typeof val === 'string' || typeof val === 'number') {
          root.style.setProperty(`--${cssKey}`, String(val));
        }
      }
    }
  }
}
